import { Dispatch, SetStateAction } from "react";

import { AI_GENERATED } from "@fronterahealth/frontera-ui-components";

interface BankRecommendation {
  id: string;
  folder: { id: string };
}

/** The recs on display in the open folder. Empty for the AI-generated folder, which has its own panel. */
export const getVisibleRecommendations = <T extends BankRecommendation>(
  recommendations: T[],
  selectedFolderId: string,
): T[] =>
  selectedFolderId === AI_GENERATED ? [] : recommendations.filter((rec) => rec.folder.id === selectedFolderId);

/** Selected recs keyed by id. Holds the rec itself so a later search that hides it cannot drop it from the count or Add. */
export type SelectedRecommendations<T> = Record<string, T>;

/** Clicking a card: adds the rec if it is not selected, removes it if it is. */
export const toggleRecommendation = <T extends BankRecommendation>(prev: SelectedRecommendations<T>, rec: T) => {
  if (prev[rec.id]) {
    const { [rec.id]: _removed, ...rest } = prev;
    return rest;
  }
  return { ...prev, [rec.id]: rec };
};

interface SelectAllVisibleParams<T extends BankRecommendation> {
  selectedFolderId: string;
  visibleRecommendations: T[];
  selected: SelectedRecommendations<T>;
  setSelected: Dispatch<SetStateAction<SelectedRecommendations<T>>>;
}

/**
 * Props for the panel's selection bar: "Select all" acts on the cards on display, and the count covers every selected
 * rec Add will insert, including ones in other folders or hidden by the current search. Undefined (no bar) until a
 * regular folder is open.
 */
export const getSelectAllVisibleProps = <T extends BankRecommendation>({
  selectedFolderId,
  visibleRecommendations,
  selected,
  setSelected,
}: SelectAllVisibleParams<T>) => {
  if (!selectedFolderId || selectedFolderId === AI_GENERATED) {
    return undefined;
  }

  const allVisibleSelected =
    visibleRecommendations.length > 0 && visibleRecommendations.every((rec) => selected[rec.id]);

  const onToggleAll = () =>
    setSelected((prev) => {
      if (visibleRecommendations.every((rec) => prev[rec.id])) {
        const visibleIds = new Set(visibleRecommendations.map((rec) => rec.id));
        return Object.fromEntries(Object.entries(prev).filter(([id]) => !visibleIds.has(id)));
      }
      return { ...prev, ...Object.fromEntries(visibleRecommendations.map((rec) => [rec.id, prev[rec.id] ?? rec])) };
    });

  return {
    selectedCount: Object.keys(selected).length,
    visibleCount: visibleRecommendations.length,
    allVisibleSelected,
    onToggleAll,
  };
};
